if (!customElements.get("auto-modal")) {
  class AutoModal extends HTMLElement {
    static getOpenModal() {
      return document.querySelector("auto-modal[open]");
    }

    static getDismissedKey() {
      return "autoModalLastDismissedAt";
    }

    static getSessionStartKey() {
      return "autoModalSessionStartedAt";
    }

    static getConfirmedSuffix() {
      return "-confirmed";
    }

    getConfirmedKey() {
      return `${this.id}${AutoModal.getConfirmedSuffix()}`;
    }

    _log(...args) {
      if (this.debug) console.log(`[AutoModal#${this.id}]`, ...args);
    }

    isConfirmed() {
      if (!this.debug && this.hasAttribute("data-confirmed")) {
        this._log("isConfirmed → true (data-confirmed attribute set)");
        return true;
      }

      try {
        const raw = window.localStorage.getItem(this.getConfirmedKey());
        this._log(`isConfirmed localStorage[${this.getConfirmedKey()}] =`, raw);
        if (!raw) {
          this._log("isConfirmed → false (no localStorage entry)");
          return false;
        }

        try {
          const entry = JSON.parse(raw);
          if (entry && entry.value === "true" && entry.savedAt) {
            if (this.debug) {
              const age = Date.now() - entry.savedAt;
              const withinTTL = age < 15 * 1000;
              this._log(`isConfirmed → ${withinTTL} (debug TTL: ${(age / 1000).toFixed(1)}s old, TTL=15s)`);
              return withinTTL;
            }
            this._log("isConfirmed → true (JSON entry, no debug TTL)");
            return true;
          }
        } catch (e) {
          // Legacy plain value
        }

        const result = raw === "true" || raw === "1";
        this._log(`isConfirmed → ${result} (legacy value: "${raw}")`);
        return result;
      } catch (e) {
        return false;
      }
    }

    markConfirmed() {
      this.setAttribute("data-confirmed", "true");
      try {
        if (!window.localStorage.getItem(this.getConfirmedKey())) {
          window.localStorage.setItem(
            this.getConfirmedKey(),
            JSON.stringify({ value: "true", savedAt: Date.now() }),
          );
        }
      } catch (e) {
        // Ignore localStorage failures
      }
    }

    getSessionStartedAt() {
      try {
        const existing = window.sessionStorage.getItem(
          AutoModal.getSessionStartKey(),
        );
        if (existing) return parseInt(existing, 10);

        const now = Date.now();
        window.sessionStorage.setItem(
          AutoModal.getSessionStartKey(),
          String(now),
        );
        return now;
      } catch (e) {
        return Date.now();
      }
    }

    hasMetExitIntentMinTime() {
      if (this.debug) return true;
      const startedAt = this.getSessionStartedAt();
      return Date.now() - startedAt >= 60 * 1000;
    }

    getLastDismissedAt() {
      try {
        const value = window.sessionStorage.getItem(
          AutoModal.getDismissedKey(),
        );
        return value ? parseInt(value, 10) : 0;
      } catch (e) {
        return 0;
      }
    }

    markDismissed() {
      try {
        window.sessionStorage.setItem(
          AutoModal.getDismissedKey(),
          String(Date.now()),
        );
      } catch (e) {
        // Ignore sessionStorage failures
      }
    }

    isInDismissCooldown() {
      const lastDismissedAt = this.getLastDismissedAt();
      if (!lastDismissedAt) return false;
      return Date.now() - lastDismissedAt < 120 * 1000;
    }

    canOpen({ ignoreDismissCooldown = false } = {}) {
      if (this.isConfirmed()) {
        this._log("canOpen → false (confirmed)");
        return false;
      }

      if (this.debug) {
        this._log("canOpen → true (debug mode, skipping dismiss/overlap checks)");
        return true;
      }

      if (!ignoreDismissCooldown && this.isInDismissCooldown()) {
        const msLeft = 120 * 1000 - (Date.now() - this.getLastDismissedAt());
        this._log(`canOpen → false (dismiss cooldown: ${(msLeft / 1000).toFixed(0)}s remaining)`);
        return false;
      }

      const openModal = AutoModal.getOpenModal();
      if (openModal && openModal !== this) {
        this._log("canOpen → false (another modal is open:", openModal.id, ")");
        return false;
      }

      this._log("canOpen → true");
      return true;
    }

    constructor() {
      super();
      this.autoOpen = !this.hasAttribute("data-closed");
      this.delay = parseInt(this.getAttribute("data-delay"));
      this.debug = this.getAttribute("data-debug");
      this.show = parseInt(this.getAttribute("data-show"));
      this.id = this.getAttribute("id");
      this.trigger = this.getAttribute("data-trigger") || "automatic";
      this.isOpen = false;
      this.exitIntentTriggered = false;

      const self = this;
      document.addEventListener(
        "DOMContentLoaded",
        self.setupButtons.bind(self),
      );

      this.style.setProperty("display", "none");

      this.querySelector('[id^="ModalClose-"]')?.addEventListener(
        "click",
        this.hide.bind(this),
      );
      this.querySelectorAll("[data-close-modal]").forEach((closer) =>
        closer.addEventListener("click", this.hide.bind(this, false)),
      );

      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE") this.hide();
      });
      this.addEventListener("click", (event) => {
        if (event.target.nodeName === "AUTO-MODAL") this.hide();
      });

      if (this.autoOpen) {
        if (this.trigger === "exit_intent") {
          this.setupExitIntent();
        } else {
          window.setTimeout(self.tryToOpen.bind(self), this.delay * 1000);
        }
      }
    }

    setupExitIntent() {
      const self = this;

      // Establish session start for cross-page gating
      self.getSessionStartedAt();

      // Only trigger on desktop (mouse-based devices)
      if (window.matchMedia("(pointer: fine)").matches) {
        document.addEventListener("mouseout", function handleMouseOut(e) {
          // Check if mouse is leaving viewport at the top
          if (
            e.clientY <= 0 &&
            self.hasMetExitIntentMinTime() &&
            !self.exitIntentTriggered &&
            !self.isOpen
          ) {
            self.exitIntentTriggered = true;
            self.tryToOpen();
          }
        });
      } else {
        // Fallback for touch devices: use scroll up detection or delayed trigger
        let lastScrollY = window.scrollY;
        let scrollUpCount = 0;

        window.addEventListener("scroll", function handleScroll() {
          if (window.scrollY < lastScrollY) {
            scrollUpCount++;
            // Trigger after scrolling up significantly from lower in the page
            if (
              self.hasMetExitIntentMinTime() &&
              scrollUpCount > 3 &&
              lastScrollY > window.innerHeight &&
              !self.exitIntentTriggered &&
              !self.isOpen
            ) {
              self.exitIntentTriggered = true;
              self.tryToOpen();
            }
          } else {
            scrollUpCount = 0;
          }
          lastScrollY = window.scrollY;
        });
      }
    }

    setupButtons() {
      const now = new Date();
      const self = this;

      const openers = document.querySelectorAll(
        '[id^="ModalOpen-' + this.id + '"],' + '[data-open="' + this.id + '"]',
      );
      if (openers) {
        const self = this;
        openers.forEach((el) => {
          el.addEventListener("click", self.open.bind(this));
        });
      }
      document
        .querySelectorAll(`[data-dismiss-open="${this.id}"]`)
        .forEach((el) =>
          el.addEventListener("click", self.dismissOpenButton.bind(self)),
        );

      let shouldOpen = true;
      if (this.isConfirmed()) {
        this._log("setupButtons → button hidden (confirmed)");
        shouldOpen = false;
      }

      const dismissedVal = localStorage.getItem("popupButtonDismissed-" + this.id);
      if (dismissedVal) {
        if (parseInt(dismissedVal) <= now.getTime()) {
          this._log("setupButtons → button hidden (popupButtonDismissed expired or set)");
          shouldOpen = false;
        } else {
          this._log(`setupButtons → popupButtonDismissed found but not yet elapsed:`, dismissedVal);
        }
      }

      this._log(`setupButtons → shouldOpen: ${shouldOpen}`);
      if (shouldOpen) {
        document
          .querySelectorAll('[data-open="' + this.id + '"]')
          .forEach((el) => {
            el.parentElement.classList.add("active");
            el.parentElement.setAttribute("tabindex", "0");
          });
      }
    }

    dismissOpenButton() {
      const now = new Date();

      localStorage.setItem(
        "popupButtonDismissed-" + this.id,
        now.getTime() + 6000,
      );

      document
        .querySelectorAll('[data-open="' + this.id + '"][data-dismissable]')
        .forEach((el) => {
          el.parentElement.classList.add("dismissed");
          el.parentElement.setAttribute("tabindex", "-1");
          el.setAttribute("disabled", "true");
        });
    }

    open() {
      this._log("open() called");
      if (!this.canOpen({ ignoreDismissCooldown: true })) {
        this._log("open() → blocked by canOpen");
        return;
      }
      this.style.removeProperty("display");
      if (this.isOpen) {
        this._log("open() → already open, skipping");
        return;
      }
      this._log("open() → opening modal");
      this.isOpen = true;
      document.body.classList.add("overflow-hidden");
      this.setAttribute("open", "");
      trapFocus(this, this.querySelector(".modal-content"));
      // window.pauseAllMedia();
      this.setStorage();
    }

    hide() {
      const wasOpen = this.isOpen;
      this.isOpen = false;
      document.body.classList.remove("overflow-hidden");
      this.removeAttribute("open");
      // window.pauseAllMedia();

      if (wasOpen) {
        this.markDismissed();
      }

      setTimeout(() => {
        this.style.setProperty("display", "none");
      }, 1000);
    }

    tryToOpen() {
      this._log("tryToOpen() called");

      if (this.isConfirmed()) {
        this._log("tryToOpen → blocked (confirmed)");
        return;
      }

      if (!this.canOpen()) {
        this._log("tryToOpen → blocked (canOpen returned false)");
        return;
      }

      const params = new URLSearchParams(window.location.search);

      if (window.IS_FS_SPLASH === true) {
        this._log("tryToOpen → blocked (splash page)");
        return;
      }

      if (params.has("openPopups")) {
        this._log("tryToOpen → opening (openPopups param)");
        this.open();
        return;
      }

      if (this.debug) {
        this._log("tryToOpen → opening (debug mode)");
        this.open();
        return;
      }

      if (this.isOpen) {
        this._log("tryToOpen → already open");
        return;
      }

      if (this.show > 0) {
        const now = new Date();
        const stored = localStorage.getItem(this.id);
        this._log(`tryToOpen show>0: localStorage[${this.id}] =`, stored);
        if (stored) {
          if (parseInt(stored) <= now.getTime()) {
            this._log("tryToOpen → opening (show interval elapsed)");
            this.open();
          } else {
            this._log(`tryToOpen → blocked (show interval not elapsed, ${((parseInt(stored) - now.getTime()) / 1000 / 60).toFixed(1)} min remaining)`);
          }
        } else {
          this._log("tryToOpen → opening (no show interval stored)");
          this.open();
        }
      } else {
        const sessionVal = window.sessionStorage.getItem(this.id);
        this._log(`tryToOpen show=0: sessionStorage[${this.id}] =`, sessionVal);
        if (!window.sessionStorage.getItem(this.id) !== "shown") {
          this._log("tryToOpen → opening (not shown this session)");
          this.open();
          window.sessionStorage.setItem(this.id, "shown");
        } else {
          this._log("tryToOpen → blocked (already shown this session)");
        }
      }
    }

    setStorage() {
      const now = new Date();
      localStorage.setItem(this.id, now.getTime() + this.show * 60 * 60 * 1000);
    }
  }

  customElements.define("auto-modal", AutoModal);
}
